import { useState, useEffect } from "react";
import styles from "./Home.module.css";
import { addToCart } from "../../utils/cartUtils";
import axios from "axios";
import { API_BASE } from "../../config/api";

export interface Product {
  id: number;
  name: string;
  description: string;
  price: number;
  type: string;
  discountActive: boolean;
  oldPrice?: number;
  discountPercentage?: number;
  category: string;
  filePath: string;
  version: string;
  moonbaseid: string;
  available: boolean;
}

/* ── Main Component ───────────────────────────────────────── */
export default function Home() {
  const [product,     setProduct]     = useState<Product | null>(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState<string | null>(null);
  const [platform,    setPlatform]    = useState<"windows" | "mac">("windows");
  const [activeSlide, setActiveSlide] = useState(0);
  const [isWiping,    setIsWiping]    = useState(false);

  const slides = [
    { image: "front112.png", title: "WaveShape",               subtitle: "Fast and Easy Sound Design" },
    { image: "back112.png",  title: "Shape Your Mix with Precision",  subtitle: "FX Rack – Granular, Chorus, EQ & More" },
  ];

  useEffect(() => {
    
    const id = setInterval(() => {
      setIsWiping(true);
      setTimeout(() => setActiveSlide((p) => (p === 0 ? 1 : 0)), 1100);
      setTimeout(() => setIsWiping(false), 2200);
    }, 6000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    axios
      .get<Product>(API_BASE + "/api/auth/products/1")
      .then((res) => setProduct(res.data))
      .catch((err) => { console.error(err); setError("Loading product failed"); })
      .finally(() => setLoading(false));
  }, []);

  const handleDownloadDemo = () => {
    const url = platform === "windows"
      ? API_BASE + "/api/download/WaveShape.zip"
      : API_BASE + "/api/download/";
    window.open(url, "_blank");
  };

  if (loading)  return <div className={styles.statusContainer}><span className={styles.loader}></span><p>Loading product...</p></div>;
  if (error)    return <div className={styles.statusContainer}><p className={styles.error}>{error}</p></div>;
  if (!product) return <div className={styles.statusContainer}><p className={styles.error}>Couldn't find product</p></div>;

  return (
    <div className={styles.container}>

      {/* ── HERO ────────────────────────────────────────────── */}
      <section className={styles.heroSection}>

        <div className={`${styles.heroWipe} ${isWiping ? styles.playing : ""}`} />

        {slides.map((slide, index) => (
          <div
            key={index}
            className={`${styles.heroSlide} ${activeSlide === index ? styles.active : ""}`}
          >
            <div className={styles.heroContentWrapper}>

              <div className={styles.heroText}>
                <div className={styles.heroLabel}>NEW SYNTHESIZER</div>
                <h1 className={styles.heroTitle}>{slide.title}</h1>
                <p className={styles.heroSubtitle}>{slide.subtitle}</p>
               {/* <div className={styles.price}>€ {product.price.toFixed(2)}</div>*/}
               <div className={styles.beta}>
                9 MB · Windows 10/11 (64-bit) · VST3 · Try the full version free for 14 days. No registration required.
                Purchasing will be available soon.
               </div>
              
                <div className={styles.buttonRow}>
                  <button className={styles.primaryButton} onClick={() => addToCart(1)}>
                    Buy (Not Available)
                  </button>
                  <button className={styles.secondaryButton} onClick={handleDownloadDemo}>
                    Download Demo 
                  </button>
                </div>
              </div>

              <div className={styles.heroImageContainer}>
                <img src={slide.image} alt="Synthesizer" className={styles.floatingImage} />
              </div>

            </div>
          </div>
        ))}

      </section>

      
{/* ── SYNTH INTRO ─────────────────────────────────────── */}
<section className={styles.synthIntro}>

  <div className={styles.synthIntroLabel}>
    THE WAVESHAPE CONCEPT
  </div>

  <h2>
    Complex Sound Design.
    <span> Made Simple.</span>
  </h2>

  <p>
    WaveShape is designed to make powerful and complex sounds
    possible in just a few clicks. By combining multiple synthesis
    techniques in one intuitive workflow, you can quickly transform
    simple oscillator sounds into rich, expressive and evolving tones.
  </p>

<p>
    Three different Morph Modes let you shape the relationship between
    the oscillators, while the pitch of the second oscillator can be
    changed independently. Send the result through the waveshaper and
    entirely new tones emerge — from powerful leads and chords to
    aggressive wobbles, basslines and growls. With the low pass filter engaged,
    the same technique can also create softer sounds, making it equally suitable 
    for pads and strings.
</p>


  <div className={styles.synthIntroHighlight}>
    <strong>Few clicks. Complex sounds.</strong>
    <span>
      Everything you need is directly in front of you —
      making WaveShape approachable for beginners while still
      offering plenty of room for experimentation.
    </span>
  </div>

</section>



      {/* ── INFO SECTION ────────────────────────────────────── */}
      <div className={styles.infoSectionWrapper}>
        <section className={styles.infoSection}>

          {/* erster Block — mit eigenem Rahmen */}
         
            <div className={styles.infoRow}>
              <div className={styles.infoImage}>
                <img src="/front112.png" alt="Synth Engine" />
              </div>
              <div className={styles.infoText}>
                <h2>Synthesizer Engine</h2>
                <p className={styles.infoDescription}>
                  Discover limitless soundscapes with the Dual-Voice architecture.
                </p>
                <div className={styles.maxWidthText}>
                <ul>
                  <li>Dual-Voice Engine with Morphing Oscillators</li>
                  <li>Over 100 Distortion Functions per Voice, such as Wavefolding,and Waveshaping</li>
                  <li>6 cross-voice modis AM, Ring, Additive, Hard Sync, Morph &amp; Period Modulation</li>
                    <li>Tremolo and Arpeggiator</li>
                  <li>200+ Premium Presets</li>
                </ul>
                </div>
              </div>
            </div>
       

          {/* zweiter Block — kein Wrapper */}
          <div className={`${styles.infoRow} ${styles.reverse}`}>
            <div className={styles.infoText}>
              <h2>Professional FX Rack</h2>
              <p className={styles.infoDescription}>
                Shape your sound with studio-quality effects.
              </p>
              <ul>
                <li>Reverb, Delay, Distortion, Chorus, Phaser, Flanger &amp; EQ</li>
                <li>Studio Compressor with Visual Feedback</li>
                <li>Realtime Spectrum Analyzer</li>
                <li>Granular Sound Engine</li>
              </ul>
            </div>
            <div className={styles.infoImage}>
              <img src="/back112.png" alt="FX Rack" />
            </div>
          </div>
        </section>

      </div>


    {/* ── YOUTUBE VIDEO ───────────────────────────────────── 
<section className={styles.youtubeSection}>
  <div className={styles.youtubeWrapper}>
    <iframe
      src="https://www.youtube.com/embed/ObbJe_i0grw"
      title="WaveShape Demo"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      allowFullScreen
    />
  </div>
</section>
*/}

    </div>


  );
}
